'use client'

import SearchPicker from '@trustoo/components/searchPicker/SearchPicker'
import { useSearchPickerContext } from '@trustoo/components/searchPicker/SearchPickerContext'
import { SearchPickerInputProps } from '@trustoo/components/searchPicker/SearchPickerInput'
import ArrowBackIcon from '@trustoo/components/ui/icon/ArrowBackIcon'
import { TAILWIND_BREAKPOINTS } from '@trustoo/constants/tailwind'
import clsx from 'clsx'
import { ReactNode } from 'react'
import { useWindowSize } from 'usehooks-ts'

export interface ResponsivePickerContentProps {
    inputFieldProps: SearchPickerInputProps
    prependedOptions?: ReactNode
    listFooterContent?: ReactNode
    portalBreakpoint?: number
    referenceId?: string
}

/**
 * ResponsivePickerContent - Responsive Picker Implementation
 *
 * **Role:**
 * A custom implementation that combines SearchPicker.Input, SearchPicker.Portal,
 * and SearchPicker.DropdownRoot with responsive behavior. On mobile (width < portalBreakpoint),
 * it uses a full-screen portal, while on desktop it uses a regular dropdown.
 *
 * **Responsibilities:**
 * - Renders input field that's always visible
 * - Conditionally renders portal (mobile) or dropdown (desktop) based on window width
 * - Provides back button in mobile portal mode
 * - Wraps everything in SearchPicker.Content for proper styling and event handling
 * - Supports prependedOptions (keyboard navigable) and listFooterContent (visual only)
 *
 * **Note:**
 * This is a custom implementation example, not part of the core SearchPicker compound.
 * It demonstrates how to build responsive pickers using the SearchPicker building blocks.
 */
const ResponsivePickerContent = ({
    inputFieldProps,
    prependedOptions,
    listFooterContent,
    portalBreakpoint = TAILWIND_BREAKPOINTS.SM,
    referenceId,
}: ResponsivePickerContentProps) => {
    const { isOpen, closePicker, options } = useSearchPickerContext()
    const windowSize = useWindowSize()
    const input = <SearchPicker.Input {...inputFieldProps} />
    const optionsList = (
        <SearchPicker.OptionsList
            prependedOptions={prependedOptions}
            listFooterContent={listFooterContent}
        />
    )
    const hasOptions = options.length > 0 || prependedOptions || listFooterContent

    return (
        <SearchPicker.Content referenceId={referenceId}>
            {input}
            {isOpen && (
                <>
                    {windowSize.width < portalBreakpoint ? (
                        <SearchPicker.Portal className="p-3 gap-3">
                            <div className="grid grid-cols-[auto_1fr] items-center flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => closePicker()}
                                    className="flex items-center justify-center p-2"
                                    aria-label="Back">
                                    <ArrowBackIcon />
                                </button>
                                {input}
                            </div>
                            <div
                                className={clsx(
                                    'flex-1 overflow-y-auto',
                                    hasOptions && 'border border-grey-500',
                                )}>
                                {optionsList}
                            </div>
                        </SearchPicker.Portal>
                    ) : (
                        <SearchPicker.DropdownRoot
                            className={clsx(hasOptions && '-mx-1 border-t border-grey-500')}>
                            {optionsList}
                        </SearchPicker.DropdownRoot>
                    )}
                </>
            )}
        </SearchPicker.Content>
    )
}

export default ResponsivePickerContent
